import type { SceneNode, VariableRegistry } from '../types/scene.js'
import type { VariableProps } from '../types/properties/variable.js'

/**
 * Resolved variable information from the variable registry
 */
export interface ResolvedVariable {
	name: string
	type: 'COLOR' | 'FLOAT' | 'BOOLEAN' | 'STRING'
	key: string // Original assetRef key for debugging
}

/**
 * Hint and asset reference usage information with context
 */
export interface HintUsage {
	name: string // Human-readable hint name OR assetRef.key for styles
	type:
		| 'COLOR'
		| 'FLOAT'
		| 'BOOLEAN'
		| 'STRING'
		| 'TEXT_STYLE'
		| 'EFFECT_STYLE'
		| 'PAINT_STYLE'
		| 'GRID_STYLE'
		| 'UNKNOWN' // Hint type or inferred asset type
	field?: string // Where the hint is applied (e.g., "STACK_SPACING", "FILL_COLOR")
}

/**
 * Resolved style information from the Internal Only Canvas
 */
export interface ResolvedStyle {
	name: string
	type: 'TEXT_STYLE' | 'EFFECT_STYLE' | 'PAINT_STYLE' | 'GRID_STYLE' | 'UNKNOWN'
	key: string // Original assetRef key for debugging
}

/**
 * Variable lookup map: assetRef.key -> variable info
 */
export type VariableLookupMap = Map<string, ResolvedVariable>

/**
 * Style lookup map: assetRef.key -> style info
 */
export type StyleLookupMap = Map<string, ResolvedStyle>

/**
 * Asset lookup map: assetRef.key -> variable or style info
 */
export type AssetLookupMap = Map<string, ResolvedVariable | ResolvedStyle>

/**
 * Build a lookup map from assetRef.key -> variable info
 *
 * @param registry - Variable registry from parser
 * @returns Map of key -> variable info
 */
export function buildVariableLookupMap(
	registry: VariableRegistry | null,
): VariableLookupMap {
	const map = new Map<string, ResolvedVariable>()

	if (!registry) {
		return map
	}

	for (const varNode of registry.variables) {
		const props = varNode.properties as VariableProps

		// Extract key and name
		const key = props.key
		const name = varNode.name
		const type = props.variableResolvedType

		if (key && name && type) {
			map.set(key, { name, type, key })
		}
	}

	return map
}

/**
 * Build a lookup map from assetRef.key -> style info
 *
 * Searches Internal Only Canvas for nodes with properties.key
 * These represent style definitions (TEXT styles, EFFECT styles, PAINT styles)
 *
 * @param registry - Variable registry from parser (contains Internal Only Canvas)
 * @returns Map of key -> style info
 */
export function buildStyleLookupMap(
	registry: VariableRegistry | null,
): StyleLookupMap {
	const map = new Map<string, ResolvedStyle>()

	if (!registry || !registry.canvas) {
		return map
	}

	// Recursively search Internal Only Canvas for style definition nodes
	function traverseForStyles(node: SceneNode): void {
		const props = node.properties as any

		// Check if this node has a style key
		if (props?.key && typeof props.key === 'string') {
			const styleType = props.styleType

			// Map Figma styleType to our style types
			let type: ResolvedStyle['type'] = 'UNKNOWN'
			if (styleType === 'TEXT') {
				type = 'TEXT_STYLE'
			} else if (styleType === 'EFFECT') {
				type = 'EFFECT_STYLE'
			} else if (styleType === 'FILL' || styleType === 'STROKE') {
				type = 'PAINT_STYLE'
			} else if (styleType === 'GRID') {
				type = 'GRID_STYLE'
			}

			// Skip UNKNOWN types - don't include them in the map
			if (type !== 'UNKNOWN') {
				map.set(props.key, {
					name: node.name,
					type,
					key: props.key,
				})
			}
		}

		// Recursively search children
		if (node.children) {
			for (const child of node.children) {
				traverseForStyles(child)
			}
		}
	}

	// Start traversal from Internal Only Canvas root
	traverseForStyles(registry.canvas)

	return map
}

/**
 * Hint reference with optional field context
 */
interface HintReference {
	key: string // assetRef key
	field?: string // variableField if available
	propertyName?: string // Property name where assetRef was found (for type inference)
}

/**
 * Recursively extract all assetRef keys from an object with field context
 * Does NOT traverse into children array (caller handles that)
 *
 * @param obj - Object to search (node properties)
 * @param references - Array to collect references into
 * @param rootContext - Root object that may contain variableField
 * @param parentPropName - Property name where assetRef was found (for type inference)
 */
export function extractAssetRefKeys(
	obj: unknown,
	references: HintReference[],
	rootContext?: unknown,
	parentPropName?: string,
): void {
	if (!obj || typeof obj !== 'object') {
		return
	}

	// Check if this is an assetRef object
	if (
		'assetRef' in obj &&
		typeof obj.assetRef === 'object' &&
		obj.assetRef !== null
	) {
		const assetRef = obj.assetRef as Record<string, unknown>
		const key = assetRef['key']
		if (typeof key === 'string') {
			// Build reference object based on what we have
			const ref: HintReference = { key }

			// Try to extract variableField from root context
			// variableField is typically a sibling of variableData at the same level
			if (
				rootContext &&
				typeof rootContext === 'object' &&
				'variableField' in rootContext
			) {
				const variableField = (rootContext as Record<string, unknown>)['variableField']
				if (typeof variableField === 'string') {
					ref.field = variableField
				}
			}

			// Add property name if available (for type inference)
			if (parentPropName !== undefined) {
				ref.propertyName = parentPropName
			}

			references.push(ref)
			return
		}
	}

	// Recursively search all properties EXCEPT 'children'
	for (const [propKey, value] of Object.entries(obj)) {
		if (propKey === 'children') {
			continue // Skip children array - caller will handle recursion
		}

		if (Array.isArray(value)) {
			for (const item of value) {
				// Pass the array item as root context (it may contain variableField)
				// Pass propKey as parent property name
				extractAssetRefKeys(item, references, item, propKey)
			}
		} else if (typeof value === 'object' && value !== null) {
			// Keep the same root context when diving deeper
			// Unless we don't have one yet, then use current object
			// Pass propKey as parent property name
			extractAssetRefKeys(value, references, rootContext || obj, propKey)
		}
	}
}

/**
 * Legacy function for backwards compatibility
 * Extracts only keys without field context
 */
export function extractAssetRefKeysSimple(obj: unknown, keys: Set<string>): void {
	const references: HintReference[] = []
	extractAssetRefKeys(obj, references)
	for (const ref of references) {
		keys.add(ref.key)
	}
}

/**
 * Collect hint usages for a single node (not including children)
 *
 * @param node - Scene node to extract hints from
 * @param lookupMap - Asset lookup map (variables and styles)
 * @returns Array of hint usages for this node only
 */
export function collectNodeHints(
	node: SceneNode,
	lookupMap: AssetLookupMap,
): HintUsage[] {
	const references: HintReference[] = []

	// Extract from THIS node's properties only (not children)
	if (node.properties) {
		extractAssetRefKeys(node.properties, references)
	}

	// Resolve references to hint usages
	const usagesMap = new Map<string, HintUsage>()
	for (const ref of references) {
		// Try to resolve from combined lookup map (variables + styles)
		const asset = lookupMap.get(ref.key)

		if (!asset) {
			// Skip unresolved assetRefs (user requirement)
			continue
		}

		// Resolved asset - use its name and type
		const usage: HintUsage = {
			name: asset.name,
			type: asset.type,
		}
		if (ref.field) {
			usage.field = ref.field
		}

		// Create unique key for deduplication (name + type + field)
		const uniqueKey = `${usage.name}::${usage.type}::${usage.field || ''}`

		if (!usagesMap.has(uniqueKey)) {
			usagesMap.set(uniqueKey, usage)
		}
	}

	// Return sorted array for consistency
	return Array.from(usagesMap.values()).sort((a, b) => {
		// Sort by name first, then by field
		if (a.name !== b.name) {
			return a.name.localeCompare(b.name)
		}
		return (a.field || '').localeCompare(b.field || '')
	})
}
